import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import ResourceEditModal from "../components/ResourceEditModal";
import api from "../lib/api";

const CATEGORY_LABELS = {
  pro: "PROs",
  mechanical: "Mechanical",
  sound_recording: "Sound Recording",
  metadata: "Metadata",
  registration: "Registration",
  artist_platforms: "Artist Platforms",
  distribution: "Distribution",
};

export default function AdminResources() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  const [resources, setResources] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | inactive
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("category");
  const [sortDir, setSortDir] = useState("asc");

  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (!user.is_admin) {
      toast.error("Not authorized");
      nav("/dashboard");
    }
  }, [loading, user, nav]);

  const load = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get("/admin/resources");
      setResources(r.data);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const filtered = useMemo(() => {
    let list = resources;
    if (filter === "active") list = list.filter((r) => r.is_active);
    else if (filter === "inactive") list = list.filter((r) => !r.is_active);
    if (categoryFilter !== "all") list = list.filter((r) => r.category === categoryFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.title?.toLowerCase().includes(q) || r.url?.toLowerCase().includes(q)
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (sortBy === "is_active") { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      if (av == null) av = "";
      if (bv == null) bv = "";
      return av > bv ? dir : av < bv ? -dir : 0;
    };
    return [...list].sort(cmp);
  }, [resources, filter, categoryFilter, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  const onNew = () => { setEditing({}); setModalOpen(true); };
  const onEdit = (r) => { setEditing(r); setModalOpen(true); };

  const onToggleActive = async (r) => {
    try {
      const updated = await api.patch(`/admin/resources/${r.id}`, { is_active: !r.is_active });
      setResources((prev) => prev.map((x) => x.id === r.id ? updated.data : x));
      toast.success(updated.data.is_active ? "Resource activated" : "Resource deactivated");
    } catch {
      toast.error("Could not update");
    }
  };

  const onSoftDelete = async (r) => {
    if (!r.is_active) return onToggleActive(r);
    try {
      await api.delete(`/admin/resources/${r.id}`);
      load();
      toast.success("Soft-deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const confirmHardDelete = async () => {
    if (!hardDeleteTarget) return;
    try {
      await api.delete(`/admin/resources/${hardDeleteTarget.id}?hard=true`);
      setHardDeleteTarget(null);
      load();
      toast.success("Permanently deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const onSaved = (saved) => {
    setResources((prev) => {
      const idx = prev.findIndex((x) => x.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
  };

  if (loading || !user?.is_admin) return null;

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="admin-resources-page" style={{ maxWidth: 1280 }}>
        <button className="btn btn-ghost" onClick={() => nav("/admin")} style={{ paddingLeft: 0, marginBottom: 6 }}>
          ← Admin Console
        </button>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="eyebrow" style={{ color: "var(--accent)" }}>— Admin Console</div>
            <h1 className="serif" style={{ fontSize: 48, lineHeight: 0.95, margin: "10px 0 0", letterSpacing: "-0.02em" }}>
              Resource Manager
            </h1>
          </div>
          <button className="btn btn-primary" onClick={onNew} data-testid="new-resource-btn">+ New Resource</button>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
          {["all", "active", "inactive"].map((f) => (
            <button
              key={f}
              className={`pill ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
          <select
            className="lq-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ width: "auto", minWidth: 200 }}
            data-testid="category-filter"
          >
            <option value="all">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <input
            className="lq-input"
            placeholder="Search title or URL…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
            data-testid="resources-search"
          />
        </div>

        {/* Table */}
        {loadingList && <div className="data-line" style={{ padding: 24 }}>Loading…</div>}
        {!loadingList && (
          <div style={{ border: "1px solid var(--border-soft)", borderRadius: 6, overflow: "hidden" }} data-testid="resources-table">
            <div style={{
              display: "grid",
              gridTemplateColumns: "minmax(180px, 1.4fr) 160px minmax(220px, 1.5fr) 90px 140px 200px",
              background: "rgba(245, 240, 232, 0.03)",
              borderBottom: "1px solid var(--border-soft)",
            }}>
              <SortHeader col="title" label="Title" sortBy={sortBy} sortDir={sortDir} onClick={() => toggleSort("title")} />
              <SortHeader col="category" label="Category" sortBy={sortBy} sortDir={sortDir} onClick={() => toggleSort("category")} />
              <Th>URL</Th>
              <SortHeader col="is_active" label="Status" sortBy={sortBy} sortDir={sortDir} onClick={() => toggleSort("is_active")} />
              <SortHeader col="updated_at" label="Updated" sortBy={sortBy} sortDir={sortDir} onClick={() => toggleSort("updated_at")} />
              <Th align="right">Actions</Th>
            </div>
            {filtered.length === 0 && (
              <div className="data-line" style={{ padding: 24 }}>No resources match your filters.</div>
            )}
            {filtered.map((r) => (
              <div
                key={r.id}
                data-testid={`resource-row-${r.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(180px, 1.4fr) 160px minmax(220px, 1.5fr) 90px 140px 200px",
                  borderBottom: "1px solid var(--border-soft)",
                  padding: "12px 14px",
                  alignItems: "center",
                  opacity: r.is_active ? 1 : 0.55,
                }}
              >
                <div className="serif" style={{ fontStyle: "italic", fontSize: 17 }}>{r.title}</div>
                <div className="data-line">{CATEGORY_LABELS[r.category] || r.category}</div>
                <a
                  href={r.url} target="_blank" rel="noopener noreferrer"
                  className="mono" style={{ fontSize: 11, color: "var(--accent)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {r.url}
                </a>
                <span className={`sub-badge ${r.is_active ? "active" : "canceled"}`} style={{ width: "fit-content" }}>
                  {r.is_active ? "Active" : "Inactive"}
                </span>
                <div className="data-line">
                  {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                </div>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button className="connection-btn" onClick={() => onEdit(r)} data-testid={`edit-${r.id}`}>Edit</button>
                  <button className="connection-btn" onClick={() => onToggleActive(r)} data-testid={`toggle-${r.id}`}>
                    {r.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button className="connection-btn danger" onClick={() => setHardDeleteTarget(r)} data-testid={`hard-delete-${r.id}`}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ResourceEditModal
        open={modalOpen}
        resource={editing}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
      />

      {/* Hard delete confirmation */}
      {hardDeleteTarget && (
        <div className="modal-backdrop" onClick={() => setHardDeleteTarget(null)} data-testid="hard-delete-modal">
          <div className="modal-card" style={{ maxWidth: 460, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow" style={{ color: "var(--red-light)" }}>— Permanent Delete</div>
            <h2 className="serif" style={{ fontSize: 26, lineHeight: 1.1, margin: "8px 0 12px" }}>
              Delete &lsquo;{hardDeleteTarget.title}&rsquo; permanently?
            </h2>
            <p style={{ color: "var(--text-mute)", fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>
              This cannot be undone. Soft-delete instead to keep the audit trail and reactivate later.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="btn btn-ghost" onClick={() => setHardDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-light" onClick={async () => {
                const r = hardDeleteTarget;
                setHardDeleteTarget(null);
                await onSoftDelete(r);
              }} data-testid="soft-delete-btn">
                Soft-delete
              </button>
              <button className="btn btn-danger" onClick={confirmHardDelete} data-testid="confirm-hard-delete-btn">
                Hard-delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Th({ children, align = "left" }) {
  return (
    <div className="mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-fade)", padding: "12px 14px", textAlign: align }}>
      {children}
    </div>
  );
}

function SortHeader({ col, label, sortBy, sortDir, onClick }) {
  const active = sortBy === col;
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent", border: 0, cursor: "pointer", padding: "12px 14px",
        textAlign: "left", color: active ? "var(--accent)" : "var(--text-fade)",
        fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
      }}
    >
      {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </button>
  );
}
